import { EmployeeTerminationType } from '@gokrew/models/employee';
import * as Yup from 'yup';
import {
  optionalDate,
  emailValidator,
  optionalString,
  requiredDateValidator,
  nonWhiteSpaceValidator,
  isWorkEmailNotSameAsUserEmail,
  nameValidator,
} from '@gokrew/ui/utility/validators';
import type { TranslationNS } from '@gokrew/models/constants';
import type { TFunction } from 'i18next';
import {
  ExpectedRecall,
  ROE_REASON_CODES,
} from '@gokrew/models/payroll-export';
import { EmployeeTerminationMode } from '@/types/employee';

// Same rule the backend enforces: a contract can't end before it starts.
// Only checked for contract employees — the field is hidden otherwise, and a
// hidden field that fails validation would block the save with no message.
const contractEndDateSchema = (t: TFunction<TranslationNS.Employee>) =>
  optionalDate().when(['employmentType', 'dateOfJoining'], {
    is: (employmentType: string | null, dateOfJoining: Date | null) =>
      employmentType === 'contract' && !!dateOfJoining,
    then: (schema) =>
      schema.min(
        Yup.ref('dateOfJoining'),
        t('validation.employee.contract_end_before_joining')
      ),
    otherwise: (schema) => schema,
  });

// Weekly hours a part-timer is contracted for. Only checked while the field
// is shown (part-time), for the same reason as the contract end date.
const partTimeWeeklyHoursSchema = (t: TFunction<TranslationNS.Employee>) =>
  Yup.number()
    .transform((value, original) => (original === '' ? undefined : value))
    .typeError(t('validation.employee.weekly_hours_invalid'))
    .nullable()
    .optional()
    .when('employmentType', {
      is: 'part_time',
      then: (schema) =>
        schema
          .min(1, t('validation.employee.weekly_hours_min'))
          .max(80, t('validation.employee.weekly_hours_max')),
    });

// The personal phone as the profile keeps it: empty, or ten digits (the
// masked input sends the digits only).
const optionalPhoneNumberSchema = (t: TFunction<TranslationNS.Employee>) =>
  optionalString().test(
    'phone-format',
    t('validation.common.invalid_phone'),
    function (value) {
      if (!value || value === '') return true;
      return /^\+?[0-9]{7,15}$/.test(value) && value.length === 10;
    }
  );

export const addEmployeeSchema = (
  t: TFunction<TranslationNS.Employee>,
  // Branch decides which policies apply and is immutable after create.
  isBranchRequired = false
) =>
  Yup.object({
    branchId: isBranchRequired
      ? Yup.string().required(t('validation.employee.branch_required'))
      : Yup.string().optional(),
    user: Yup.object({
      // No email (D157): the address is not asked for.
      hasNoEmail: Yup.boolean().optional(),
      email: Yup.string().when('hasNoEmail', {
        is: true,
        then: (schema) => schema.optional(),
        otherwise: () =>
          emailValidator(t('validation.common.invalid_email'))
            .required(t('validation.common.email_required'))
            .trim(),
      }),
      firstName: nameValidator()
        .min(2, t('validation.employee.first_name_min'))
        .max(40, t('validation.employee.first_name_max'))
        .required(t('validation.employee.first_name_required'))
        .trim(),
      lastName: nameValidator()
        .min(2, t('validation.employee.last_name_min'))
        .max(40, t('validation.employee.last_name_max'))
        .trim(),
      phoneNumber: optionalPhoneNumberSchema(t),
    }),
    designationId: Yup.string().optional(),
    departmentId: Yup.string().optional(),
    employeeReferenceId: Yup.string().optional(),
    employmentType: Yup.string().optional(),
    contractEndDate: contractEndDateSchema(t),
    partTimeWeeklyHours: partTimeWeeklyHoursSchema(t),
    dateOfJoining: Yup.date()
      .test(
        'max-joining-date',
        t('validation.employee.joining_date_max'),
        (value) => {
          if (!value) return true;
          const maxDate = new Date();
          maxDate.setMonth(maxDate.getMonth() + 12);
          return value <= maxDate;
        }
      )
      .typeError(t('validation.common.invalid_date'))
      .optional(),
    leaveEligibleFrom: optionalDate().when('dateOfJoining', {
      is: (dateOfJoining: Date | null) => !!dateOfJoining,
      then: (schema) =>
        schema.min(
          Yup.ref('dateOfJoining'),
          t('validation.employee.leave_before_joining')
        ),
      otherwise: (schema) => schema,
    }),
    workPhone: Yup.string()
      .length(10, t('validation.common.phone_10_digits'))
      .matches(/^\+?[0-9]{7,15}$/, t('validation.common.invalid_phone'))
      .optional()
      .trim(),
    workEmail: emailValidator(t('validation.common.invalid_email'))
      .optional()
      .trim()
      .test(
        'work-email-not-same',
        t('validation.employee.work_email_same'),
        function (value) {
          return isWorkEmailNotSameAsUserEmail(value, this);
        }
      ),
    isProbationEnabled: Yup.boolean(),
    probationPeriod: Yup.number()
      .nullable()
      .when('isProbationEnabled', {
        is: true,
        then: (schema) =>
          schema
            .required(t('validation.common.probation_required'))
            .min(1)
            .max(365, t('validation.common.probation_max')),
      }),
  });

export const employeePersonalInformationSchema = (
  t: TFunction<TranslationNS.Employee>
) =>
  Yup.object({
    personalEmail: emailValidator(t('validation.common.invalid_email'))
      .nullable()
      .optional(),
    phoneNumber: optionalPhoneNumberSchema(t),
    gender: Yup.string().optional(),
    dateOfBirth: Yup.date()
      .optional()
      .max(new Date(), t('validation.employee.dob_future')),
  });

export const employeeClothingSizesSchema = (
  t: TFunction<TranslationNS.Employee>
) =>
  Yup.object({
    shirtSize: optionalString().max(50, t('validation.employee.size_max')),
    pantsSize: optionalString().max(50, t('validation.employee.size_max')),
    pantsLength: optionalString().max(50, t('validation.employee.size_max')),
  });

export const employeeWorkInformationSchema = (
  t: TFunction<TranslationNS.Employee>
) =>
  Yup.object({
    employeeReferenceId: optionalString().max(
      40,
      t('validation.employee.employee_id_max')
    ),
    designationId: optionalString(),
    departmentId: optionalString(),
    dateOfJoining: optionalDate().test(
      'max-joining-date',
      t('validation.employee.joining_date_max'),
      (value) => {
        if (!value) return true;
        const maxDate = new Date();
        maxDate.setMonth(maxDate.getMonth() + 12);
        return value <= maxDate;
      }
    ),
    leaveEligibleFrom: optionalDate().when('dateOfJoining', {
      is: (dateOfJoining: Date | null) => !!dateOfJoining,
      then: (schema) =>
        schema.min(
          Yup.ref('dateOfJoining'),
          t('validation.employee.leave_before_joining')
        ),
      otherwise: (schema) => schema,
    }),

    status: optionalString(),
    workEmail: emailValidator(t('validation.common.invalid_email'))
      .optional()
      .nullable(),
    workPhone: optionalString()
      .matches(/^\+?[0-9]{7,15}$/, t('validation.common.invalid_phone'))
      .min(10, t('validation.common.phone_min_10'))
      .max(15, t('validation.common.phone_max_15')),
    employmentType: optionalString(),
    contractEndDate: contractEndDateSchema(t),
    partTimeWeeklyHours: partTimeWeeklyHoursSchema(t),
  });

export const terminateEmployeeSchema = (
  t: TFunction<TranslationNS.Employee>,
  options: { roeRequired?: boolean } = {}
) =>
  Yup.object({
    mode: Yup.string()
      .oneOf(Object.values(EmployeeTerminationMode))
      .required(t('validation.termination.mode_required')),

    terminationType: Yup.string()
      .oneOf(
        Object.values(EmployeeTerminationType),
        t('validation.termination.type_required')
      )
      .required(t('validation.termination.type_required')),

    terminationDate: Yup.date()
      .typeError(t('validation.common.invalid_date'))
      .when('mode', {
        is: EmployeeTerminationMode.SCHEDULED,
        then: (schema) =>
          schema
            .required(t('validation.termination.termination_date_required'))
            .min(
              new Date(new Date().setHours(0, 0, 0, 0)),
              t('validation.termination.scheduled_past_not_allowed')
            ),
        otherwise: (schema) => schema.optional().nullable(),
      }),

    scheduledFor: Yup.date()
      .nullable()
      .typeError(t('validation.common.invalid_date'))
      .when('mode', {
        is: EmployeeTerminationMode.SCHEDULED,

        then: (schema) =>
          schema
            .required(t('validation.termination.scheduled_for_required'))
            .test(
              'scheduled-future-and-after-effective',
              t('validation.termination.scheduled_future_required'),
              function (value) {
                if (!value) return true;
                if (value < new Date()) return false;

                const terminationDate = this.parent.terminationDate as
                  Date | undefined;
                if (terminationDate && value < terminationDate) {
                  return this.createError({
                    message: t(
                      'validation.termination.scheduled_before_effective_date'
                    ),
                  });
                }

                return true;
              }
            ),

        otherwise: (schema) => schema.nullable().optional(),
      }),

    reason: Yup.string()
      .trim()
      .max(500, t('validation.termination.reason_max')),

    // ROE inputs (D94): required only for companies that use payroll
    // (saved payroll settings); optional for everyone else.
    roeReasonCode: options.roeRequired
      ? Yup.string()
          .oneOf(
            [...ROE_REASON_CODES],
            t('validation.termination.roe_code_required')
          )
          .required(t('validation.termination.roe_code_required'))
      : Yup.string().oneOf(
          ['', ...ROE_REASON_CODES],
          t('validation.termination.roe_code_required')
        ),

    lastDayWorked: Yup.string().optional(),

    // ROE block 17 / 18 (payroll v1 PR 4): optional.
    payInLieuAmount: Yup.number()
      .transform((value, original) => (original === '' ? undefined : value))
      .min(0, t('validation.termination.amount_min'))
      .optional(),
    severanceAmount: Yup.number()
      .transform((value, original) => (original === '' ? undefined : value))
      .min(0, t('validation.termination.amount_min'))
      .optional(),
    roeComment: Yup.string()
      .max(160, t('validation.termination.roe_comment_max'))
      .optional(),

    expectedRecall: options.roeRequired
      ? Yup.string()
          .oneOf(
            Object.values(ExpectedRecall),
            t('validation.termination.recall_required')
          )
          .required(t('validation.termination.recall_required'))
      : Yup.string().oneOf(
          ['', ...Object.values(ExpectedRecall)],
          t('validation.termination.recall_required')
        ),

    expectedRecallDate: Yup.string().when('expectedRecall', {
      is: ExpectedRecall.Date,
      then: (schema) =>
        schema
          .required(t('validation.termination.recall_date_required'))
          .test(
            'recall-after-last-day',
            t('validation.termination.recall_after_last_day'),
            function (value) {
              const lastDay = this.parent.lastDayWorked as string | undefined;
              return !value || !lastDay || value > lastDay;
            }
          ),
      otherwise: (schema) => schema.optional(),
    }),
  });

export const scheduledTerminationSchema = (
  t: TFunction<TranslationNS.Employee>
) =>
  Yup.object({
    scheduledFor: Yup.date()
      .required(t('validation.termination.scheduled_date_required'))
      .typeError(t('validation.common.invalid_date')),

    reason: Yup.string()
      .trim()
      .max(500, t('validation.termination.reason_max')),
  });

/** Every part of an address is optional (JP, 2026-09-30). */
export const employeeAddressSchema = (t: TFunction<TranslationNS.Employee>) =>
  Yup.object({
    addressLine_1: nonWhiteSpaceValidator().nullable().optional(),
    addressLine_2: nonWhiteSpaceValidator().nullable().optional(),
    city: nameValidator().nullable().optional(),
    provinceId: Yup.string().nullable().optional(),
    postalCode: nonWhiteSpaceValidator()
      .nullable()
      .optional()
      .matches(/^[A-Za-z0-9][A-Za-z0-9\s-]{2,10}[A-Za-z0-9]$/, {
        message: t('validation.address.postal_invalid'),
        excludeEmptyString: true,
      }),
    countryCode: Yup.string().nullable().optional(),
  });

export const employeeFamilySchema = (t: TFunction<TranslationNS.Employee>) =>
  Yup.object({
    name: nameValidator().required(t('validation.family.name_required')).trim(),
    relation: Yup.string().required(t('validation.family.relation_required')),
    gender: Yup.string().required(t('validation.family.gender_required')),
    dateOfBirth: Yup.string().optional(),
    email: emailValidator(t('validation.common.invalid_email')).optional(),
  });

export const employeeEducationSchema = (t: TFunction<TranslationNS.Employee>) =>
  Yup.object({
    educationLevel: Yup.string().required(
      t('validation.education.level_required')
    ),
    institutionName: Yup.string()
      .trim()
      .required(t('validation.education.institution_name_required')),
    course: Yup.string()
      .trim()
      .required(t('validation.education.course_required')),
    startDate: requiredDateValidator(t('validation.education.start_required'))
      .typeError(t('validation.common.invalid_date'))
      .max(new Date(), t('validation.education.start_future'))
      .nullable(),
    endDate: requiredDateValidator(t('validation.education.end_required'))
      .min(Yup.ref('startDate'), t('validation.education.end_before_start'))
      .nullable(),
  });

export const employeeDocumentSchema = (t: TFunction<TranslationNS.Employee>) =>
  Yup.object({
    documentTypeId: Yup.string().required(
      t('validation.document.type_required')
    ),
    comments: Yup.string()
      .optional()
      .max(200, t('validation.document.comments_max')),
  });

export const employeeAssetAssignSchema = (
  t: TFunction<TranslationNS.Employee>
) =>
  Yup.object({
    modelId: Yup.string().required(t('validation.asset.model_required')),

    assetId: Yup.string().required(t('validation.asset.asset_unit_required')),

    assignedDate: requiredDateValidator(
      t('validation.asset.assigned_date_required')
    )
      .typeError(t('validation.common.invalid_date'))
      .max(new Date(), t('validation.asset.assigned_date_future')),

    notes: Yup.string()
      .max(250, t('validation.common.max_length', { count: 250 }))
      .nullable(),
  });

export const employeeAssetUpdateSchema = (
  t: TFunction<TranslationNS.Employee>
) =>
  Yup.object({
    returnDueDate: optionalDate()
      .typeError(t('validation.common.invalid_date'))
      .when('assignedDate', {
        is: (assignedDate: Date | null | undefined) => !!assignedDate,
        then: (schema) =>
          schema.min(
            Yup.ref('assignedDate'),
            t('validation.asset.return_date_before_assigned')
          ),
        otherwise: (schema) => schema,
      }),
    assignedDate: optionalDate(),
    notes: Yup.string()
      .max(250, t('validation.common.max_length', { count: 250 }))
      .nullable(),
  });

/** Hours worked per week on a salary: empty (the schedule) or 0.5 to 168. */
const weeklyHoursRule = (t: TFunction<TranslationNS.Employee>) =>
  Yup.mixed<number | ''>().test(
    'weekly-hours',
    t('employee_salary.weekly_hours.invalid'),
    (value) =>
      value === '' ||
      value == null ||
      (Number(value) >= 0.5 && Number(value) <= 168)
  );

export const EmployeeSalarySchema = (
  t: TFunction<TranslationNS.Employee>,
  dateOfJoining?: Date | null
) =>
  Yup.object({
    amount: Yup.number()
      .typeError(t('validation.salary.amount_invalid'))
      .required(t('validation.salary.amount_required'))
      .min(0.01, t('validation.salary.amount_positive'))
      .max(999999999.99, t('validation.salary.amount_too_large')),
    currency: Yup.string().required(
      t('validation.common.required', { field: 'Currency' })
    ),

    payType: Yup.string().required(t('validation.salary.pay_type_required')),

    effectiveFrom: Yup.date()
      .required(t('validation.salary.effective_required'))
      .typeError(t('validation.common.invalid_date'))
      .test(
        'after-joining',
        t('validation.salary.effective_before_joining'),
        function (value) {
          if (!value || !dateOfJoining) return true;
          return new Date(value) >= new Date(dateOfJoining);
        }
      ),

    effectiveTo: Yup.date()
      .nullable()
      .min(
        Yup.ref('effectiveFrom'),
        t('validation.salary.effective_before_start')
      ),

    notes: Yup.string().nullable(),
    weeklyHours: weeklyHoursRule(t),
  });

/**
 * Adjust compensation: a new amount, a basis, an effective date after the
 * current salary's start, and a reason.
 */
export const AdjustSalarySchema = (
  t: TFunction<TranslationNS.Employee>,
  currentFrom?: string | null
) =>
  Yup.object({
    amount: Yup.number()
      .typeError(t('validation.salary.amount_invalid'))
      .required(t('validation.salary.amount_required'))
      .min(0.01, t('validation.salary.amount_positive'))
      .max(999999999.99, t('validation.salary.amount_too_large')),
    payType: Yup.string().required(t('validation.salary.pay_type_required')),
    currency: Yup.string().required(
      t('employee_salary.adjust.currency_required')
    ),
    effectiveFrom: Yup.string()
      .required(t('validation.salary.effective_required'))
      .test(
        'after-current',
        t('employee_salary.adjust.after_current'),
        (value) => !value || !currentFrom || value.slice(0, 10) > currentFrom
      ),
    reason: Yup.string().required(t('employee_salary.adjust.reason_required')),
    weeklyHours: weeklyHoursRule(t),
  });

export const EmployeeBonusSchema = (t: TFunction<TranslationNS.Employee>) =>
  Yup.object({
    category: Yup.string()
      .oneOf(['bonus', 'commission', 'other'])
      .required(t('validation.bonus.type_required')),
    periodLabel: Yup.string().max(64),

    valueType: Yup.string()
      .oneOf(['fixed', 'percentage'])
      .required(t('validation.bonus.value_type_required')),

    amount: Yup.number().when('valueType', {
      is: 'fixed',
      then: (schema) =>
        schema
          .required(t('validation.bonus.amount_required'))
          .min(0, t('validation.bonus.amount_min')),
      otherwise: (schema) => schema.optional(),
    }),

    currency: Yup.string().when('valueType', {
      is: 'fixed',
      then: (schema) =>
        schema.required(t('validation.bonus.currency_required')),
      otherwise: (schema) => schema.optional(),
    }),

    percentage: Yup.number().when('valueType', {
      is: 'percentage',
      then: (schema) =>
        schema
          .required(t('validation.bonus.percentage_required'))
          .min(0, t('validation.bonus.percentage_min'))
          .max(100, t('validation.bonus.percentage_max')),
      otherwise: (schema) => schema.optional(),
    }),

    effectiveDate: Yup.date()
      .required(t('validation.bonus.effective_required'))
      .typeError(t('validation.common.invalid_date')),

    notes: Yup.string().nullable(),
  });

export const BonusTypeSchema = (t: TFunction<TranslationNS.Employee>) =>
  Yup.object({
    name: Yup.string()
      .required(t('validation.bonus_type.name_required'))
      .min(2, t('validation.bonus_type.name_min'))
      .max(50, t('validation.bonus_type.name_max')),
  });

export const BenefitEnrollmentSchema = (t: (key: string) => string) =>
  Yup.object({
    planId: Yup.string().required(
      t('benefit_enrollment.validation.plan_required')
    ),
    employeeContribPercent: Yup.number()
      .typeError(t('benefit_enrollment.validation.must_be_number'))
      .min(0, t('benefit_enrollment.validation.min_zero'))
      .max(100, t('benefit_enrollment.validation.max_hundred'))
      .nullable(),
    employerMatchPercent: Yup.number()
      .typeError(t('benefit_enrollment.validation.must_be_number'))
      .min(0, t('benefit_enrollment.validation.min_zero'))
      .max(100, t('benefit_enrollment.validation.max_hundred'))
      .nullable(),
    matchCapPercent: Yup.number()
      .typeError(t('benefit_enrollment.validation.must_be_number'))
      .min(0, t('benefit_enrollment.validation.min_zero'))
      .max(100, t('benefit_enrollment.validation.max_hundred'))
      .nullable(),
    enrollmentDate: Yup.date()
      .required(t('benefit_enrollment.validation.enrollment_date_required'))
      .typeError(t('benefit_enrollment.validation.invalid_date')),
    endDate: Yup.date()
      .nullable()
      .min(
        Yup.ref('enrollmentDate'),
        t('benefit_enrollment.validation.end_before_enrollment')
      )
      .typeError(t('benefit_enrollment.validation.invalid_date')),
    notes: Yup.string()
      .nullable()
      .max(500, t('benefit_enrollment.validation.notes_max')),
  });

export const employeeImportFormSchema = (
  t: TFunction<TranslationNS.Employee>
) =>
  Yup.object().shape({
    file: Yup.mixed()
      .required(t('validation.import.file_required'))
      .test(
        'fileFormat',
        t('validation.import.file_invalid'),
        (value) => value instanceof File && value.name.endsWith('.csv')
      ),
  });

export const deactivateWalletSchema = Yup.object({
  resetWallet: Yup.boolean().required(),
});

/**
 * Grant (positive) or remove (negative) days on one balance. A removal is a
 * correction: it needs a reason and cannot take what is left below zero
 * (ModulMax, 2026-10-15; the API enforces both).
 */
export const grantWalletSchema = (
  t: TFunction<TranslationNS.Employee>,
  remainingDays: number
) =>
  Yup.object({
    amount: Yup.number()
      .typeError(t('leave_wallet.form.grant.validation.number'))
      .required(t('leave_wallet.form.grant.validation.required'))
      .notOneOf([0], t('leave_wallet.form.grant.validation.not_zero'))
      .min(-365, t('leave_wallet.form.grant.validation.min'))
      .max(365, t('leave_wallet.form.grant.validation.max'))
      .test(
        'not-below-zero',
        t('leave_wallet.form.grant.validation.below_zero', {
          balance: Math.max(Math.round(remainingDays * 100) / 100, 0),
        }),
        (value) => value == null || value >= 0 || remainingDays + value >= -1e-9
      ),
    comment: Yup.string().when('amount', {
      is: (amount: number | undefined) => amount != null && amount < 0,
      then: (schema) =>
        schema
          .trim()
          .required(t('leave_wallet.form.grant.validation.reason_required')),
      otherwise: (schema) => schema.optional(),
    }),
  });

export const EmployeeTasksSchema = (t: TFunction) =>
  Yup.object({
    name: Yup.string().trim().required(t('validation.task_name_required')),
    assigneeType: Yup.string().required(t('validation.assign_to_required')),
    targetId: Yup.string().when('assigneeType', {
      is: (value: string) => value !== 'employee_dynamic',
      then: (schema) => schema.required(t('validation.target_required')),
      otherwise: (schema) => schema.notRequired(),
    }),
    taskListId: Yup.string().required(t('validation.task_list_required')),
    priority: Yup.string().required(t('validation.priority_required')),
    requiresApproval: Yup.boolean(),
    dueDate: Yup.string().nullable(),
    description: Yup.string().nullable(),
    attachments: Yup.array().nullable(),
  });

export const TasksDetailSchema = Yup.object({
  status: Yup.string().required(),
  comment: Yup.string(),
});

// Status changes on a flow task now go through explicit action buttons
// (getFlowTaskActions), not a form field — this dialog only needs to
// validate the comment box.
export const CommentSchema = Yup.object({
  comment: Yup.string(),
});

export const JoiningDateSchema = (t: TFunction) =>
  Yup.object({
    dateOfJoining: Yup.date()
      .required(t('validation.employee.joining_date_required'))
      .typeError(t('validation.common.invalid_date'))
      .test(
        'max-joining-date',
        t('validation.employee.joining_date_max'),
        (value) => {
          if (!value) return true;

          const maxDate = new Date();
          maxDate.setMonth(maxDate.getMonth() + 12);

          return value <= maxDate;
        }
      ),
  });
